import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@trashlab/sitesapp/src/@/lib/utils';

interface AnimatedTextBlockProps {
  children: React.ReactNode;
  className?: string;
  threshold?: number;
  delay?: number;
  as?: React.ElementType;
}

/**
 * AnimatedTextBlock - Animates a text block as a single unit
 * Use this component to wrap groups of text elements that should animate together
 * Images and backgrounds should be placed outside this component
 */
export const AnimatedTextBlock: React.FC<AnimatedTextBlockProps> = ({
  children,
  className,
  threshold = 0.1,
  delay = 0,
  as: Component = 'div',
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (delay > 0) {
            setTimeout(() => {
              setIsVisible(true);
            }, delay);
          } else {
            setIsVisible(true);
          }
          
          // Once animated, stop observing
          if (ref.current) {
            observer.unobserve(ref.current);
          }
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -150px 0px',
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, delay]);

  return (
    <Component
      ref={ref}
      className={cn(
        'text-content-block',
        isVisible ? 'animate-fade-in-bottom' : 'opacity-0',
        className
      )}
    >
      {children}
    </Component>
  );
};

export default AnimatedTextBlock; 